import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { AuthErrorModel } from '@telialt/models/src/ErrorModel';

import { AuthToken } from './model';

export const RETRY_FLAG = 'Axios-Retry';
const INVALID_TOKEN_ESHOP_ERROR_TYPE = 'InvalidTokenError';
const INVALID_TOKEN_SS_ERROR = 'invalid_token';

interface InternalAxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
    'Axios-Retry': boolean;
}

interface AxiosErrorWithRetry<T> extends AxiosError<T> {
    config: InternalAxiosRequestConfigWithRetry;
}

const isAuthenticationError = (error: AxiosError<AuthErrorModel>) =>
    error.response?.status === 401 &&
    (error.response?.data?.errors?.[0]?.type === INVALID_TOKEN_ESHOP_ERROR_TYPE ||
        error.response?.data?.error === INVALID_TOKEN_SS_ERROR);

const isAuthorizationError = (error: AxiosError<AuthErrorModel>) =>
    error.response?.status === 403 && error.response?.data?.errors?.[0]?.type === 'ForbiddenError';

const isRetryRequest = (error: AxiosErrorWithRetry<AuthErrorModel>) => !!error.config?.[RETRY_FLAG];

export const responseErrorInterceptor =
    (axios: AxiosInstance, getToken: (force?: boolean) => Promise<AuthToken>) =>
    async (error: AxiosErrorWithRetry<AuthErrorModel>) => {
        if ((!isAuthenticationError(error) && !isAuthorizationError(error)) || isRetryRequest(error)) {
            return Promise.reject(error);
        }

        try {
            const { accessToken, tokenType } = await getToken(true);
            if (error?.config) {
                error.config.headers.Authorization = `${tokenType ? tokenType : 'bearer'} ${accessToken}`;
                error.config = {
                    ...error.config,
                    'Axios-Retry': true,
                };
            }

            return axios.request(error.config);
        } catch (getTokenError) {
            // eslint-disable-next-line no-console
            console.error(getTokenError);

            return Promise.reject(error);
        }
    };
